import { Phone, PhoneCall, Mic, Volume2, RotateCw, Check, Shield, Clock } from "lucide-react";
import { T } from "../theme";
import { Eyebrow, Card, Pill } from "../components";

function formatTime(iso: string | null): string {
  if (!iso) return "--:--";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** WAKE CALL — live escalation state from the worker */
export function WakeCall({ escalation }: { escalation: any }) {
  const isActive = escalation?.is_active === 1;
  const step = escalation?.step ?? 0;
  const nextCall = escalation?.next_call_at;

  const steps = [
    { label: "Armed for morning", done: step >= 0, icon: Phone },
    { label: "Wake call placed", done: step >= 1, icon: PhoneCall },
    { label: "Echo verification", done: step >= 2, icon: Mic },
    { label: "Briefing delivered", done: step >= 3, icon: Volume2 },
    { label: "Re-verify scheduled", done: step >= 4, icon: RotateCw },
  ];

  return (
    <div style={{ padding: "18px 16px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Eyebrow>wake call {isActive ? "· active" : "· standby"}</Eyebrow>
        {isActive ? (
          <Pill color={T.dawn} bg="rgba(255,107,69,.14)">live · step {step}</Pill>
        ) : (
          <Pill color={T.dim} bg="rgba(154,146,188,.12)">standby</Pill>
        )}
      </div>

      <div style={{ textAlign: "center", margin: "26px 0 20px" }}>
        <div style={{ position: "relative", width: 96, height: 96, margin: "0 auto" }}>
          {isActive && (
            <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${T.dawn}`, animation: "zbRing 2s ease-out infinite" }} />
          )}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isActive
                ? `radial-gradient(circle at 35% 30%, ${T.gold}, ${T.dawn})`
                : `radial-gradient(circle at 35% 30%, ${T.dim}, ${T.edge})`,
            }}
          >
            <PhoneCall size={34} color={T.night} />
          </div>
        </div>
        <div style={{ fontSize: 17, color: T.paper, fontWeight: 600, marginTop: 14 }}>
          {isActive ? "The phone is ringing" : "Alarm armed · waiting for wake time"}
        </div>
        {nextCall && (
          <div style={{ fontSize: 12, color: T.mist, marginTop: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <Clock size={12} />
            Next call: {formatTime(nextCall)}
          </div>
        )}
      </div>

      <Card>
        <Eyebrow>verification</Eyebrow>
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
          {steps.map((s) => (
            <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: s.done ? "rgba(130,220,164,.15)" : isActive && !s.done ? "rgba(255,180,84,.15)" : "rgba(154,146,188,.08)",
                  animation: isActive && !s.done ? "zbPulse 1.6s infinite" : "none",
                }}
              >
                <s.icon size={13} color={s.done ? T.mint : isActive && !s.done ? T.gold : T.dim} />
              </div>
              <span style={{ fontSize: 13, color: s.done ? T.paper : isActive && !s.done ? T.gold : T.dim, fontWeight: isActive && !s.done ? 600 : 400 }}>{s.label}</span>
              {s.done && <Check size={13} color={T.mint} style={{ marginLeft: "auto" }} />}
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ marginTop: 12, background: "rgba(255,107,69,.06)", borderColor: "rgba(255,107,69,.25)" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Shield size={14} color={T.dawn} style={{ marginTop: 1 }} />
          <span style={{ fontSize: 11.5, color: T.mist, lineHeight: 1.5 }}>
            Hanging up doesn't dismiss. Re-verify calls back in 10 min — escalation resumes if you've gone horizontal.
          </span>
        </div>
      </Card>
    </div>
  );
}
